import { useEffect, useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useMatch } from 'react-router-dom'

import { IContinuousRoute } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import { openNewSavedStatesDontMatchSystemScopeRoutesSnackbar } from 'app/entities/store/snackbarSlice'

import { SavedStateAssessmentOperators } from 'features/RiskManagement/Assessments/Components/SavedStateAssesssmentOperators'
import SavedStateInfoButton from 'features/RiskManagement/Assessments/Components/SavedStateInfoButton'
import { SavedStateInfoHeaderText } from 'features/RiskManagement/Assessments/Components/SavedStateInfoHeaderText'
import SavedStateToolbar from 'features/RiskManagement/Assessments/Components/Toolbar/SavedStateToolbar'
import WISInfoButton from 'features/RiskManagement/Assessments/Scenarios/WISInfoButton'
import { WISAssessmentStatusCode } from 'features/RiskManagement/Assessments/WISAssessmentStatusCode'
import { selectSavedFilter } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import RiskViewShell from 'features/RiskManagement/Components/RiskViewShell'

import { SavedStatePageContent } from './SavedStatePageContent'
import { ISavedStateViewProps } from './types'

const SystemView = ({
  qraBasePath,
  stateId,
  assessmentId
}: ISavedStateViewProps) => {
  const dispatch = useAppDispatch()

  const matchState = useMatch('/risk-management/assessments/states/:stateId/*')
  const matchStateWithAssessment = useMatch(
    '/risk-management/assessments/states/:stateId/assessments/:assessmentId/*'
  )

  const url = matchStateWithAssessment?.pathnameBase ?? matchState!.pathnameBase

  const { currentState, stateStatus } = useGetQRASavedStateQuery(
    stateId ?? skipToken,
    {
      selectFromResult: (result) => ({
        currentState: result.currentData,
        stateStatus: result.status
      })
    }
  )
  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const { currentAssessmentWithScope, assessmentStatus } =
    useGetQRAAssessmentQuery(assessmentId ?? skipToken, {
      selectFromResult: (result) => ({
        currentAssessmentWithScope: result.currentData,
        assessmentStatus: result.status
      })
    })

  const currentAssessment = currentState?.assessments?.find(
    (assessment) => assessment.id.toString() === assessmentId
  )

  const isCompletedAssessment =
    currentAssessment?.statusCode === WISAssessmentStatusCode.Completed

  const isErroredAssessment =
    currentAssessment?.statusCode === WISAssessmentStatusCode.Error

  const localFilter = useAppSelector(selectSavedFilter)

  useEffect(() => {
    // Check if assessmentId or currentAssessmentWithScope is null or undefined
    // If either is missing, skip the comparison and wait until they are available
    if (!assessmentId || !currentAssessmentWithScope) {
      return
    }

    if (assessmentId) {
      let routesNotMatch = true
      let classLocationNotMatch = true
      let consequenceLevelNotMatch = true
      let riskLevelNotMatch = true

      if (
        localFilter?.segments.length > 0 &&
        currentAssessmentWithScope?.assessmentContinuousRoutes
      ) {
        const assessmentRoutes =
          currentAssessmentWithScope?.assessmentContinuousRoutes
            .map((route) => {
              return {
                continuousRouteId: route.continuousRouteId,
                fromMeasure: route.fromMeasure,
                toMeasure: route.toMeasure
              }
            })
            .sort((a, b) => a.continuousRouteId - b.continuousRouteId)

        const filteredRoutes = localFilter.segments
          .map((route: IContinuousRoute) => {
            return {
              continuousRouteId: route.id,
              fromMeasure: route.fromMeasure,
              toMeasure: route.toMeasure
            }
          })
          .sort((a, b) => a.continuousRouteId - b.continuousRouteId)

        routesNotMatch = !(
          assessmentRoutes.length === filteredRoutes.length &&
          assessmentRoutes.every(
            (route, index) =>
              route.continuousRouteId ===
                filteredRoutes[index].continuousRouteId &&
              Math.floor(route.fromMeasure) ===
                Math.floor(filteredRoutes[index].fromMeasure) &&
              Math.ceil(route.toMeasure) ===
                Math.ceil(filteredRoutes[index].toMeasure)
          )
        )
      } else {
        if (
          systemScopeFilterProfile?.continuousRoutes &&
          currentAssessmentWithScope?.assessmentContinuousRoutes
        ) {
          const assessmentRoutes =
            currentAssessmentWithScope?.assessmentContinuousRoutes
              .map((route) => {
                return {
                  continuousRouteId: route.continuousRouteId,
                  fromMeasure: route.fromMeasure,
                  toMeasure: route.toMeasure
                }
              })
              .sort((a, b) => a.continuousRouteId - b.continuousRouteId)

          const systemScopeRoutes = systemScopeFilterProfile.continuousRoutes
            .map((route: IContinuousRoute) => {
              return {
                continuousRouteId: route.id,
                fromMeasure: route.fromMeasure,
                toMeasure: route.toMeasure
              }
            })
            .sort((a, b) => a.continuousRouteId - b.continuousRouteId)

          routesNotMatch = !(
            assessmentRoutes.length === systemScopeRoutes.length &&
            assessmentRoutes.every(
              (route, index) =>
                route.continuousRouteId ===
                  systemScopeRoutes[index].continuousRouteId &&
                Math.floor(route.fromMeasure) ===
                  Math.floor(systemScopeRoutes[index].fromMeasure) &&
                Math.ceil(route.toMeasure) ===
                  Math.ceil(systemScopeRoutes[index].toMeasure)
            )
          )
        } else {
          routesNotMatch = false
        }
      }

      if (
        localFilter?.classLocations.length > 0 ||
        (currentAssessmentWithScope?.assessmentClassLocations &&
          currentAssessmentWithScope?.assessmentClassLocations.length > 0)
      ) {
        if (currentAssessmentWithScope) {
          const assessmentClassLocations =
            currentAssessmentWithScope.assessmentClassLocations
              .map((classLocation) => classLocation.code)
              .sort()
          const filterClassLocations = localFilter.classLocations
            .map((classLocation) => classLocation.code)
            .sort()

          classLocationNotMatch = !(
            assessmentClassLocations.length === filterClassLocations.length &&
            assessmentClassLocations.every(
              (value, index) => value === filterClassLocations[index]
            )
          )
        } else {
          classLocationNotMatch = true
        }
      } else {
        classLocationNotMatch = false
      }

      if (
        localFilter?.hmca.length > 0 ||
        (currentAssessmentWithScope?.assessmentConsequenceLevels &&
          currentAssessmentWithScope?.assessmentConsequenceLevels.length > 0)
      ) {
        if (currentAssessmentWithScope) {
          const assessmentConsequenceLevels =
            currentAssessmentWithScope.assessmentConsequenceLevels
              .map((consequenceLevels) => consequenceLevels.code)
              .sort()
          const filterConsequenceLevels = localFilter.hmca
            .map((consequenceLevels) => consequenceLevels.code)
            .sort()

          consequenceLevelNotMatch = !(
            assessmentConsequenceLevels.length ===
              filterConsequenceLevels.length &&
            assessmentConsequenceLevels.every(
              (value, index) => value === filterConsequenceLevels[index]
            )
          )
        } else {
          consequenceLevelNotMatch = true
        }
      } else {
        consequenceLevelNotMatch = false
      }

      if (
        localFilter?.riskLevels.length > 0 ||
        (currentAssessmentWithScope?.assessmentRiskLevels &&
          currentAssessmentWithScope?.assessmentRiskLevels.length > 0)
      ) {
        if (currentAssessmentWithScope) {
          const assessmentRiskLevels =
            currentAssessmentWithScope.assessmentRiskLevels
              .map((riskLevels) => riskLevels.code)
              .sort()
          const filterRiskLevels = localFilter.riskLevels
            .map((riskLevels) => riskLevels.code)
            .sort()

          riskLevelNotMatch = !(
            assessmentRiskLevels.length === filterRiskLevels.length &&
            assessmentRiskLevels.every(
              (value, index) => value === filterRiskLevels[index]
            )
          )
        } else {
          riskLevelNotMatch = true
        }
      } else {
        riskLevelNotMatch = false
      }

      if (
        routesNotMatch ||
        classLocationNotMatch ||
        consequenceLevelNotMatch ||
        riskLevelNotMatch
      ) {
        dispatch(
          openNewSavedStatesDontMatchSystemScopeRoutesSnackbar({
            id: '',
            savedStateName: currentState ? currentState.name : '',
            currentAssessment: currentAssessmentWithScope
          })
        )
      }
    }
  }, [
    dispatch,
    systemScopeFilterProfile?.continuousRoutes,
    assessmentId,
    currentAssessmentWithScope,
    localFilter,
    currentState
  ])

  const isAssessmentQueryDone =
    !assessmentId || ['fulfilled', 'rejected'].includes(assessmentStatus)

  const isStateQueryDone = stateStatus === 'fulfilled'
  const isLoading = !(isAssessmentQueryDone && isStateQueryDone)

  const pageHeaderText = `State ${currentState?.name ?? stateId}`

  const asterisk = currentAssessment?.isPublished ? '' : '*'
  const pageHeaderAssessmentText = currentAssessment
    ? currentAssessment.name + asterisk
    : undefined

  const titleSuffix = pageHeaderAssessmentText ? (
    <>
      <span className='mx-1 text-2xl'>/</span>
      <span className='text-lg text-gray-900'>
        {pageHeaderAssessmentText}
      </span>
    </>
  ) : undefined

  const memoizedPageContent = useMemo(() => {
    return (
      <SavedStatePageContent
        viewType='system'
        qraBasePath={qraBasePath}
        qraViewBasePath={url}
        stateId={stateId}
        assessmentId={assessmentId}
        currentState={currentState}
        currentAssessment={currentAssessment}
        isLoading={isLoading}
        isCompletedAssessment={isCompletedAssessment}
      />
    )
  }, [
    assessmentId,
    currentAssessment,
    currentState,
    isCompletedAssessment,
    isLoading,
    qraBasePath,
    stateId,
    url
  ])

  const pageSubheading = 'System View'

  return (
    <RiskViewShell
      breadcrumbs={pageSubheading}
      title={pageHeaderText}
      titleSuffix={titleSuffix}
      actions={
        <div className='flex items-center'>
          <div className='flex items-center text-gray-700'>
            <SavedStateInfoHeaderText
              stateOrAssessment={currentAssessment ?? currentState}
            />
            {assessmentId == null ? (
              <SavedStateInfoButton />
            ) : (
              <WISInfoButton
                qraName={currentState?.name}
                currentAssessment={currentAssessmentWithScope}
              />
            )}
          </div>
          {(isCompletedAssessment || isErroredAssessment) && (
            <SavedStateAssessmentOperators
              assessmentId={currentAssessment?.id.toString()}
            />
          )}
        </div>
      }
      toolbar={
        <SavedStateToolbar
          qraBasePath={qraBasePath}
          qraViewBasePath={url}
          stateId={currentState?.id.toString()}
          baselineId={currentState?.baselineId}
          assessmentId={currentAssessment?.id.toString()}
        />
      }
    >
      {memoizedPageContent}
    </RiskViewShell>
  )
}

export default SystemView
